"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { getStoreStatus, type StoreStatus } from "@/lib/store-status";
import { getEcommerceByDomainClient } from "@/lib/nexus-app.client";

type StoreStatusCtx = {
  isOpen: boolean;
  isPaused: boolean;
  setIsOpen: (v: boolean) => void;
  applyStatus: (status: StoreStatus) => void;
  refresh: () => Promise<StoreStatus | null>;
};

const StoreStatusContext = createContext<StoreStatusCtx | null>(null);

export function StoreStatusProvider({
  initialIsOpen,
  initialIsPaused = false,
  children,
}: {
  initialIsOpen: boolean;
  initialIsPaused?: boolean;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(initialIsOpen);
  const [isPaused, setIsPaused] = useState(initialIsPaused);

  const applyStatus = useCallback((status: StoreStatus) => {
    setIsOpen(status.isOpen);
    setIsPaused(status.reason === "pos-offline");
  }, []);

  const refresh = useCallback(async () => {
    try {
      const store = await getEcommerceByDomainClient(window.location.host);
      const status = getStoreStatus(store);
      applyStatus(status);
      return status;
    } catch {
      return null;
    }
  }, [applyStatus]);

  const value = useMemo(
    () => ({ isOpen, isPaused, setIsOpen, applyStatus, refresh }),
    [isOpen, isPaused, applyStatus, refresh]
  );
  return (
    <StoreStatusContext.Provider value={value}>
      {children}
    </StoreStatusContext.Provider>
  );
}

export function useStoreStatus() {
  const ctx = useContext(StoreStatusContext);
  if (!ctx) {
    throw new Error("useStoreStatus must be used within StoreStatusProvider");
  }
  return ctx;
}

export function useStoreRecheck() {
  const { refresh } = useStoreStatus();
  const [checking, setChecking] = useState(false);
  const [stillPaused, setStillPaused] = useState(false);

  const check = useCallback(async () => {
    setChecking(true);
    const status = await refresh();
    setStillPaused(status?.reason === "pos-offline");
    setChecking(false);
  }, [refresh]);

  return { checking, stillPaused, check };
}
